/**
 * EntrancePage — Dukkan home.
 * Sections: Hero · Near you · Browse all · Trending now · Last visits
 * Browse / Yours inner tabs.  Desktop sidebar via BottomTabBar.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore, getAuthHeaders } from '../../stores/authStore'
import { useShopStore } from '../../stores/shopStore'
import { useInstallPrompt } from '../../hooks/useInstallPrompt'
import { apiUrl } from '../../config'
import PublicShopBrowser from '../Shops/PublicShopBrowser'
import { getGuestId } from '../../utils/guestIdentity'
import { useHomePageData } from '../../hooks/useHomePageData'
import type { ShopCard as ShopCardData } from '../../hooks/useHomePageData'
import './EntrancePage.css'

const UNREAD_COUNT_KEY = 'dukkan_unread_count'
const UNREAD_EVENT = 'dukkan:unread-count'

// ─── Types ─────────────────────────────────────────────────────

interface DkShop {
  id: string
  name: string
  category?: string
  address?: string
  posterUrl?: string
  lastVisit?: string
}

// ─── Helpers ───────────────────────────────────────────────────
function shopHue(id: string): number {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) & 0xffff
  return h % 360
}

function posterSrc(id: string, token?: string | null, direct?: string): string {
  if (direct) return direct
  const base = apiUrl(`/api/shops/${encodeURIComponent(id)}/poster`)
  return token ? `${base}?token=${encodeURIComponent(token)}` : base
}

function fromShopCard(s: ShopCardData): DkShop {
  return {
    id: s.realShopId || s.shopId,
    name: s.shopName,
    category: s.category,
    address: s.address,
    posterUrl: s.posterUrl,
    lastVisit: s.lastVisit,
  }
}

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.asin(Math.sqrt(a))
}

// ─── Hero Carousel ─────────────────────────────────────────────
interface HeroSlide {
  id: string
  label: string
  titlePlain: string
  titleGrad: string
  sub: string
}

const HERO_SLIDES: HeroSlide[] = [
  {
    id: 'D',
    label: 'Scan to 3D',
    titlePlain: 'Scan any storefront.',
    titleGrad: 'Walk inside in 3D.',
    sub: 'One photo is all it takes — Dukkan builds the shop in 3D, tags every item, and opens the door in seconds.',
  },
  {
    id: 'C',
    label: 'Scan to 3D',
    titlePlain: 'Scan any store.',
    titleGrad: 'Walk inside in 3D.',
    sub: 'Every shelf, every product — captured.',
  },
  {
    id: 'A',
    label: 'Scan to 3D',
    titlePlain: 'Scan any store.',
    titleGrad: 'Walk inside in 3D.',
    sub: 'Hold up your camera — Dukkan does the rest.',
  },
]

function particleSeedsHc(n: number, seed: number) {
  const out: Array<{ left: number; size: number; delay: number; dx: number; dy: number }> = []
  let x = seed * 9301 + 49297
  const rng = () => { x = (x * 9301 + 49297) % 233280; return x / 233280 }
  for (let i = 0; i < n; i++) {
    out.push({
      left: 10 + rng() * 85,
      size: 2.5 + rng() * 3,
      delay: rng() * 3.2,
      dx: (rng() - 0.5) * 30,
      dy: -(30 + rng() * 40),
    })
  }
  return out
}

function PremiumStore() {
  return (
    <svg viewBox="0 0 124 90" className="dk-hc-store-svg" aria-hidden>
      <defs>
        <linearGradient id="hcWinGlow" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff3c4" stopOpacity="0.95"/>
          <stop offset="60%" stopColor="#ffb55a" stopOpacity="0.85"/>
          <stop offset="100%" stopColor="#8a5a2a" stopOpacity="0.9"/>
        </linearGradient>
        <linearGradient id="hcDoorGlow" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff3c4"/>
          <stop offset="100%" stopColor="#d4a35a"/>
        </linearGradient>
        <linearGradient id="hcFacade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fffaf0"/>
          <stop offset="100%" stopColor="#f1e7cf"/>
        </linearGradient>
        <linearGradient id="hcBrick" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#b8916b"/>
          <stop offset="100%" stopColor="#8a6648"/>
        </linearGradient>
        <pattern id="hcAwnStripe" x="0" y="0" width="11" height="14" patternUnits="userSpaceOnUse">
          <rect width="11" height="14" fill="#1f2a73"/>
          <rect x="5.5" width="5.5" height="14" fill="#faf4e1"/>
        </pattern>
        <pattern id="hcAwnStripeDoor" x="0" y="0" width="8" height="10" patternUnits="userSpaceOnUse">
          <rect width="8" height="10" fill="#22d3ee"/>
          <rect x="4" width="4" height="10" fill="#faf4e1"/>
        </pattern>
        <radialGradient id="hcWarmSpill" cx="0.5" cy="1" r="0.7">
          <stop offset="0%" stopColor="#ffd97a" stopOpacity=".55"/>
          <stop offset="100%" stopColor="#ffd97a" stopOpacity="0"/>
        </radialGradient>
        <filter id="hcSignGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="0.8"/>
        </filter>
      </defs>

      <ellipse className="dk-hc-reveal-color" cx="62" cy="86" rx="50" ry="2.5" fill="#0a0f24" opacity="0.18"/>
      <ellipse className="dk-hc-win-light" cx="62" cy="82" rx="28" ry="6" fill="url(#hcWarmSpill)"/>

      {/* wireframe outlines */}
      <g>
        <rect className="dk-hc-reveal-stroke" x="16" y="28" width="92" height="50"/>
        <rect className="dk-hc-reveal-stroke" x="22" y="46" width="26" height="30" style={{animationDelay:'.15s'}}/>
        <rect className="dk-hc-reveal-stroke" x="76" y="46" width="26" height="30" style={{animationDelay:'.25s'}}/>
        <rect className="dk-hc-reveal-stroke" x="52" y="46" width="20" height="32" style={{animationDelay:'.35s'}}/>
        <rect className="dk-hc-reveal-stroke" x="18" y="14" width="88" height="12" style={{animationDelay:'.45s'}}/>
        <rect className="dk-hc-reveal-stroke" x="18" y="38" width="88" height="8" style={{animationDelay:'.55s'}}/>
      </g>

      {/* solid storefront */}
      <g className="dk-hc-reveal-color">
        <rect x="14" y="76" width="96" height="6" fill="url(#hcBrick)"/>
        <line x1="14" y1="79" x2="110" y2="79" stroke="#5b3e29" strokeWidth="0.4" opacity=".7"/>
        <rect x="16" y="28" width="92" height="48" fill="url(#hcFacade)"/>
        <rect x="16" y="28" width="4" height="48" fill="#e7dcbf"/>
        <rect x="104" y="28" width="4" height="48" fill="#e7dcbf"/>
        <rect x="48" y="46" width="2" height="30" fill="#e7dcbf"/>
        <rect x="74" y="46" width="2" height="30" fill="#e7dcbf"/>
        <rect x="14" y="26" width="96" height="3" fill="#1f2a73"/>
        {/* left window */}
        <rect x="22" y="46" width="26" height="30" fill="#1f2a73"/>
        <rect x="23.2" y="47.2" width="23.6" height="27.6" fill="url(#hcWinGlow)"/>
        <line x1="35" y1="47" x2="35" y2="75" stroke="#1f2a73" strokeWidth="0.5"/>
        <line x1="23" y1="60" x2="48" y2="60" stroke="#1f2a73" strokeWidth="0.5"/>
        <rect className="dk-hc-win-light" x="23.2" y="47.2" width="23.6" height="27.6" fill="#fff3c4" opacity=".25"/>
        <circle cx="29" cy="54" r="2" fill="#1f2a73" opacity=".55"/>
        <path d="M26.5,56.5 L26.5,67 Q26.5,71 29,71 Q31.5,71 31.5,67 L31.5,56.5 Z" fill="#1f2a73" opacity=".55"/>
        {/* right window */}
        <rect x="76" y="46" width="26" height="30" fill="#1f2a73"/>
        <rect x="77.2" y="47.2" width="23.6" height="27.6" fill="url(#hcWinGlow)"/>
        <line x1="89" y1="47" x2="89" y2="75" stroke="#1f2a73" strokeWidth="0.5"/>
        <line x1="77" y1="60" x2="102" y2="60" stroke="#1f2a73" strokeWidth="0.5"/>
        <rect className="dk-hc-win-light" x="77.2" y="47.2" width="23.6" height="27.6" fill="#fff3c4" opacity=".25"/>
        <rect x="79" y="63" width="3" height="4" fill="#1f2a73" opacity=".55"/>
        <rect x="83" y="62" width="2.5" height="5" fill="#1f2a73" opacity=".5"/>
        <rect x="87" y="64" width="2" height="3" fill="#1f2a73" opacity=".55"/>
        <rect x="91" y="62" width="3" height="5" fill="#1f2a73" opacity=".5"/>
        {/* door */}
        <rect x="52" y="46" width="20" height="32" fill="#1f2a73"/>
        <rect x="53.2" y="47.2" width="17.6" height="22" fill="url(#hcDoorGlow)"/>
        <line x1="62" y1="47" x2="62" y2="69" stroke="#1f2a73" strokeWidth="0.4"/>
        <line x1="53.2" y1="58" x2="70.8" y2="58" stroke="#1f2a73" strokeWidth="0.4"/>
        <rect className="dk-hc-win-light" x="53.2" y="47.2" width="17.6" height="22" fill="#fff3c4" opacity=".3"/>
        <rect x="53.2" y="69.5" width="17.6" height="8.3" fill="#152057"/>
        <rect x="60.5" y="60" width="1" height="3" fill="#d4af37"/>
        <rect x="62.5" y="60" width="1" height="3" fill="#d4af37"/>
        <rect x="50" y="78" width="24" height="2" fill="#7a614b"/>
        {/* planters */}
        <rect x="44" y="71" width="4" height="7" fill="#7a614b" stroke="#0a0f24" strokeWidth="0.3"/>
        <ellipse cx="46" cy="70" rx="3" ry="2.5" fill="#4a7c4e"/>
        <rect x="76" y="71" width="4" height="7" fill="#7a614b" stroke="#0a0f24" strokeWidth="0.3"/>
        <ellipse cx="78" cy="70" rx="3" ry="2.5" fill="#4a7c4e"/>
      </g>

      {/* awning */}
      <g className="dk-hc-reveal-color">
        <rect x="18" y="38" width="88" height="8" fill="url(#hcAwnStripe)"/>
        <rect x="18" y="45" width="88" height="1.5" fill="#0a0f24" opacity=".35"/>
        <path d="M18,46 Q23.5,52 29,46 Q34.5,52 40,46 Q45.5,52 51,46 Q56.5,52 62,46 Q67.5,52 73,46 Q78.5,52 84,46 Q89.5,52 95,46 Q100.5,52 106,46 L106,46 Z" fill="url(#hcAwnStripe)"/>
        <rect x="50" y="34" width="24" height="6" fill="url(#hcAwnStripeDoor)"/>
        <path d="M50,40 Q53,44 56,40 Q59,44 62,40 Q65,44 68,40 Q71,44 74,40 L74,40 Z" fill="url(#hcAwnStripeDoor)"/>
      </g>

      {/* hanging lanterns */}
      <g className="dk-hc-reveal-color">
        <line x1="22" y1="34" x2="22" y2="36" stroke="#0a0f24" strokeWidth="0.5"/>
        <rect x="20.5" y="36" width="3" height="4" fill="#1f2a73" stroke="#0a0f24" strokeWidth="0.3"/>
        <rect className="dk-hc-win-light" x="20.5" y="36" width="3" height="4" fill="#ffd97a"/>
        <line x1="102" y1="34" x2="102" y2="36" stroke="#0a0f24" strokeWidth="0.5"/>
        <rect x="100.5" y="36" width="3" height="4" fill="#1f2a73" stroke="#0a0f24" strokeWidth="0.3"/>
        <rect className="dk-hc-win-light" x="100.5" y="36" width="3" height="4" fill="#ffd97a"/>
      </g>

      {/* sign */}
      <g className="dk-hc-reveal-color">
        <rect x="18" y="14" width="88" height="12" rx="1" fill="#faf4e1" stroke="#1f2a73" strokeWidth="0.8"/>
        <circle cx="20" cy="16" r=".6" fill="#1f2a73"/>
        <circle cx="104" cy="16" r=".6" fill="#1f2a73"/>
        <g className="dk-hc-sign-glow">
          <circle cx="34" cy="20" r="2.4" fill="#22d3ee"/>
          <text x="40" y="23.4" fontFamily="Georgia, serif" fontSize="9" fontWeight="900" fill="#0a0f24" letterSpacing=".4">DUKKAN</text>
          <text x="80" y="22.5" fontFamily="monospace" fontSize="3.4" fontWeight="700" fill="#1f2a73" letterSpacing="1" opacity=".7">·EST 26·</text>
        </g>
      </g>

      {/* scan-complete tick */}
      <g className="dk-hc-tick">
        <circle cx="110" cy="32" r="4.5" fill="#22d3ee" filter="url(#hcSignGlow)"/>
        <path d="M107.8,32 L109.6,33.8 L112.3,30.6" fill="none" stroke="#0a0f24" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      </g>
    </svg>
  )
}

function HeroVarA() {
  const pts = useMemo(() => particleSeedsHc(7, 11), [])
  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <div className="dk-hc-bg-sky"/>
      <div className="dk-hc-bg-sidewalk"/>
      <div className="dk-hc-store-wrap">
        <PremiumStore/>
      </div>
      <div className="dk-hc-beam"/>
      <div className="dk-hc-beam-line"/>
      {pts.map((p, i) => (
        <span
          key={i}
          className="dk-hc-pt"
          aria-hidden
          style={{
            left: p.left + '%',
            width: p.size + 'px',
            height: p.size + 'px',
            animationDelay: p.delay + 's',
            '--ptp': `translate(${p.dx}px, ${p.dy}px)`,
          } as React.CSSProperties}
        />
      ))}
    </div>
  )
}

function HeroVarC() {
  const dots = useMemo(() => {
    const result: Array<{ fx: number; fy: number; x: number; y: number; delay: number }> = []
    let s = 7
    const rng = () => { s = (s * 9301 + 49297) % 233280; return s / 233280 }
    for (let i = 0; i < 32; i++) {
      result.push({ fx: (rng() - 0.5) * 140, fy: (rng() - 0.5) * 100 - 16, x: 18 + rng() * 88, y: 32 + rng() * 40, delay: rng() * 1.6 })
    }
    return result
  }, [])
  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <div className="dk-hc-bg-sky"/>
      <div className="dk-hc-bg-sidewalk"/>
      {dots.map((d, i) => (
        <span
          key={i}
          className="dk-hc-dot"
          aria-hidden
          style={{
            left: d.x + 'px',
            top: d.y + 'px',
            animationDelay: d.delay + 's',
            '--from-x': d.fx + 'px',
            '--from-y': d.fy + 'px',
          } as React.CSSProperties}
        />
      ))}
      <div className="dk-hc-store-wrap">
        <PremiumStore/>
      </div>
    </div>
  )
}

function HeroVarD() {
  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <div className="dk-hc-vd-bg"/>
      <div className="dk-hc-vd-portal">
        <div className="dk-hc-vd-tag">Enter</div>
        <div style={{ position: 'absolute', inset: 0 }}>
          <PremiumStore/>
        </div>
      </div>
    </div>
  )
}

function HeroCarousel({ onScan }: { onScan: () => void }) {
  const [idx, setIdx] = useState(0)
  const [sliding, setSliding] = useState(false)

  useEffect(() => {
    const timer = setInterval(() => { setSliding(true) }, 7200)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    if (!sliding) return
    const t = setTimeout(() => {
      setIdx(i => (i + 1) % HERO_SLIDES.length)
      setSliding(false)
    }, 480)
    return () => clearTimeout(t)
  }, [sliding])

  const nextIdx = (idx + 1) % HERO_SLIDES.length
  const slide = HERO_SLIDES[idx]
  const nextSlide = HERO_SLIDES[nextIdx]

  return (
    <>
      {/* Icon — push-slide track */}
      <div className="dk-hero-icon-wrap">
        <div className={`dk-hero-icon-track${sliding ? ' dk-hero-icon-track--slide' : ''}`}>
          <div className="dk-hero-icon-slot">
            <div className="dk-hero-icon-inner">
              {idx === 0 && <HeroVarD key="D"/>}
              {idx === 1 && <HeroVarC key="C"/>}
              {idx === 2 && <HeroVarA key="A"/>}
            </div>
          </div>
          {sliding && (
            <div className="dk-hero-icon-slot">
              <div className="dk-hero-icon-inner">
                {nextIdx === 0 && <HeroVarD key="nD"/>}
                {nextIdx === 1 && <HeroVarC key="nC"/>}
                {nextIdx === 2 && <HeroVarA key="nA"/>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Text — push-slide track */}
      <div className="dk-hero-text">
        <div className={`dk-hero-text-track${sliding ? ' dk-hero-text-track--slide' : ''}`}>
          <div className="dk-hero-text-slide">
            <div className="dk-hero-label">{slide.label}</div>
            <div className="dk-hero-title">
              {slide.titlePlain}{' '}
              <span className="dk-hero-gradient">{slide.titleGrad}</span>
            </div>
            <div className="dk-hero-sub">{slide.sub}</div>
            <button className="dk-hero-cta" onClick={onScan}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 8h3l1.5-2.2h7L17 8h3a1 1 0 0 1 1 1v9.5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z"/>
                <circle cx="12" cy="13.5" r="3.8"/>
              </svg>
              Start scanning
            </button>
            <div className="dk-hero-slide-dots" aria-hidden>
              {HERO_SLIDES.map((s, i) => (
                <div key={s.id} className={`dk-hero-slide-dot${i === (sliding ? nextIdx : idx) ? ' dk-hero-slide-dot--active' : ''}`}/>
              ))}
            </div>
          </div>
          {sliding && (
            <div className="dk-hero-text-slide">
              <div className="dk-hero-label">{nextSlide.label}</div>
              <div className="dk-hero-title">
                {nextSlide.titlePlain}{' '}
                <span className="dk-hero-gradient">{nextSlide.titleGrad}</span>
              </div>
              <div className="dk-hero-sub">{nextSlide.sub}</div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

// ─── Section head ──────────────────────────────────────────────
interface SectionHeadProps {
  title: string
  subtitle?: string
  cta?: string
  onCta?: () => void
}
function DkSectionHead({ title, subtitle, cta, onCta }: SectionHeadProps) {
  return (
    <div className="dk-section-head">
      <div>
        <div className="dk-section-title">{title}</div>
        {subtitle && <div className="dk-section-sub">{subtitle}</div>}
      </div>
      {cta && (
        <button className="dk-section-cta" onClick={onCta}>{cta} →</button>
      )}
    </div>
  )
}

// ─── Plaque Card ───────────────────────────────────────────────
interface PlaqueCardProps {
  shop: DkShop
  token: string | null
  onNavigate: (path: string) => void
}

function DkPlaqueCard({ shop, token, onNavigate }: PlaqueCardProps) {
  const [posterErr, setPosterErr] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null)
  const [following, setFollowing] = useState(false)
  const [followLoading, setFollowLoading] = useState(false)
  const dotsRef = useRef<HTMLButtonElement>(null)

  const hue = shopHue(shop.id)
  const tintL = `oklch(0.24 0.07 ${hue})`
  const tintD = `oklch(0.18 0.08 ${hue})`
  const accentTint = `oklch(0.6 0.18 ${hue})`
  const src = posterErr ? null : posterSrc(shop.id, token, shop.posterUrl)

  useEffect(() => {
    if (!token) return
    fetch(apiUrl(`/api/shops/${encodeURIComponent(shop.id)}/following`), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setFollowing(d.following) })
      .catch(() => {})
  }, [shop.id, token])

  const handleFollow = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!token || followLoading) return
    setFollowLoading(true)
    try {
      const res = await fetch(apiUrl(`/api/shops/${encodeURIComponent(shop.id)}/follow`), {
        method: following ? 'DELETE' : 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) setFollowing(f => !f)
    } catch { /* ignore */ } finally { setFollowLoading(false) }
  }

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation()
    const url = `${window.location.origin}/shops/${shop.id}`
    if (navigator.share) {
      try { await navigator.share({ title: shop.name, url }) } catch { /* dismissed */ }
    } else {
      try { await navigator.clipboard.writeText(url) } catch { /* ignore */ }
    }
  }

  const openMenu = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (dotsRef.current) {
      const r = dotsRef.current.getBoundingClientRect()
      const menuH = 140
      const menuW = 170
      const top = r.bottom + 6 + menuH > window.innerHeight ? r.top - menuH - 6 : r.bottom + 6
      const left = Math.min(Math.max(r.left, 8), window.innerWidth - menuW - 8)
      setMenuPos({ top, left })
    }
    setMenuOpen(o => !o)
  }

  return (
    <div
      className="dk-card-wrap"
      onClick={() => onNavigate(`/?shopid=${shop.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') onNavigate(`/?shopid=${shop.id}`) }}
    >
      {/* Floating medallion */}
      <div className="dk-card-medallion">
        <div className="dk-card-medallion-inner">
          {src ? (
            <img
              src={src}
              alt={shop.name}
              className="dk-card-poster"
              loading="lazy"
              onError={() => setPosterErr(true)}
            />
          ) : null}
          <div className="dk-card-poster-fallback" style={{ zIndex: src ? -1 : 1 }}>🏪</div>
        </div>
      </div>

      {/* Card body */}
      <div
        className="dk-card-body"
        style={{ '--card-tl': tintL, '--card-td': tintD } as React.CSSProperties}
      >
        {/* Corner stitching */}
        <svg className="dk-card-stitch dk-card-stitch--tl" width="36" height="36" viewBox="0 0 36 36" aria-hidden>
          <path d="M2 18 L18 2" stroke="rgba(231,236,255,0.25)" strokeWidth="1" strokeDasharray="2 3" fill="none" />
        </svg>
        <svg className="dk-card-stitch dk-card-stitch--br" width="36" height="36" viewBox="0 0 36 36"
          style={{ position: 'absolute', bottom: 6, right: 6 }} aria-hidden>
          <path d="M18 34 L34 18" stroke="rgba(231,236,255,0.25)" strokeWidth="1" strokeDasharray="2 3" fill="none" />
        </svg>

        {/* 3-dots button */}
        <button
          ref={dotsRef}
          className="dk-card-dots-btn"
          onClick={openMenu}
          aria-label="More options"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="5" cy="12" r="2"/>
            <circle cx="12" cy="12" r="2"/>
            <circle cx="19" cy="12" r="2"/>
          </svg>
        </button>

        {/* Content */}
        <div className="dk-card-content">
          {shop.category && (
            <div className="dk-card-cat" style={{ color: accentTint }}>{shop.category}</div>
          )}
          <div className="dk-card-name">{shop.name}</div>
          {shop.address && (
            <div className="dk-card-address">{shop.address}</div>
          )}
          <div className="dk-card-spacer" />

          {/* Perforated band */}
          <div className="dk-card-band">
            <div className="dk-card-notch dk-card-notch--l" />
            <div className="dk-card-notch dk-card-notch--r" />
            <div className="dk-card-band-row">
              <span />
              <div
                className="dk-card-arrow"
                style={{ background: accentTint }}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"/><path d="m13 6 6 6-6 6"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dropdown menu */}
      {menuOpen && menuPos && (
        <>
          <div className="dk-card-menu-backdrop" onClick={(e) => { e.stopPropagation(); setMenuOpen(false) }} />
          <div
            className="dk-card-menu"
            style={{ top: menuPos.top, left: menuPos.left }}
            onClick={e => e.stopPropagation()}
          >
            <button className="dk-card-menu-item" onClick={(e) => { setMenuOpen(false); handleShare(e) }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
              Share
            </button>
            {token && (
              <button
                className={`dk-card-menu-item${following ? ' dk-card-menu-item--active' : ''}`}
                onClick={(e) => { setMenuOpen(false); handleFollow(e) }}
                disabled={followLoading}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                {following ? 'Unfollow' : 'Follow'}
              </button>
            )}
            <button
              className="dk-card-menu-item"
              onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onNavigate(`/reviews/${shop.id}`) }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              Reviews
            </button>
            <button
              className="dk-card-menu-item dk-card-menu-item--danger"
              onClick={(e) => { e.stopPropagation(); setMenuOpen(false) }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/>
              </svg>
              Report
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// ─── Trend Card ────────────────────────────────────────────────
interface TrendCardProps { shop: DkShop; rank: number; token: string | null; onNavigate: (p: string) => void }
function DkTrendCard({ shop, rank, token, onNavigate }: TrendCardProps) {
  const [posterErr, setPosterErr] = useState(false)
  const src = posterErr ? null : posterSrc(shop.id, token, shop.posterUrl)

  return (
    <div className="dk-trend-card" onClick={() => onNavigate(`/?shopid=${shop.id}`)}>
      <div className="dk-trend-poster">
        {src && (
          <img src={src} alt={shop.name} loading="lazy" onError={() => setPosterErr(true)} />
        )}
        <div className="dk-trend-poster-fallback" style={{ zIndex: src ? -1 : 1 }}>🏪</div>
        <div className="dk-trend-rank">{rank}</div>
        <div className="dk-trend-up">
          <svg width="9" height="9" viewBox="0 0 12 12">
            <path d="M2 9 L6 4 L8 6 L10 3" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7 3 L10 3 L10 6" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          trending
        </div>
      </div>
      <div className="dk-trend-info">
        <div className="dk-trend-name">{shop.name}</div>
        <div className="dk-trend-meta">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="#f59e0b" stroke="none">
            <path d="m12 3.5 2.8 5.8 6.4.9-4.6 4.5 1.1 6.4L12 18l-5.7 3.1 1.1-6.4-4.6-4.5 6.4-.9L12 3.5Z"/>
          </svg>
          4.8 · {shop.category || 'Shop'}
        </div>
      </div>
    </div>
  )
}

// ─── Last Visit Card ───────────────────────────────────────────
interface LastVisitProps { shop: DkShop; token: string | null; onNavigate: (p: string) => void }
function DkLastVisitCard({ shop, token, onNavigate }: LastVisitProps) {
  const [posterErr, setPosterErr] = useState(false)
  const src = posterErr ? null : posterSrc(shop.id, token, shop.posterUrl)

  return (
    <div className="dk-lastvisit-card" onClick={() => onNavigate(`/?shopid=${shop.id}`)}>
      <div className="dk-lastvisit-thumb">
        {src && (
          <img src={src} alt={shop.name} loading="lazy" onError={() => setPosterErr(true)} />
        )}
        <div className="dk-lastvisit-thumb-fallback" style={{ zIndex: src ? -1 : 1 }}>🏪</div>
      </div>
      <div className="dk-lastvisit-body">
        <div className="dk-lastvisit-label">Last visit</div>
        <div className="dk-lastvisit-name">{shop.name}</div>
        <div className="dk-lastvisit-cat">{shop.category || 'Shop'}</div>
        <button
          className="dk-lastvisit-resume"
          onClick={(e) => { e.stopPropagation(); onNavigate(`/?shopid=${shop.id}`) }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="#062130" stroke="none">
            <path d="M7 4.5v15l13-7.5L7 4.5Z"/>
          </svg>
          Resume
        </button>
      </div>
    </div>
  )
}


// ═══════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════════════════════════════
export default function EntrancePage() {
  const { t } = useTranslation('entrance')
  const navigate = useNavigate()
  const user = useAuthStore(s => s.user)
  const token = useAuthStore(s => s.token)
  const setAuth = useAuthStore(s => s.setAuth)

  const [_restoring, setRestoring] = useState(true)
  const [unreadMsgs, setUnreadMsgs] = useState(() => {
    try {
      const stored = localStorage.getItem(UNREAD_COUNT_KEY)
      const parsed = stored ? Number(stored) : 0
      return Number.isFinite(parsed) ? parsed : 0
    } catch {
      return 0
    }
  })
  const [homeSearch, setHomeSearch] = useState('')
  const [userPos, setUserPos] = useState<{ lat: number; lng: number } | null>(null)
  const [locationDenied, setLocationDenied] = useState(false)

  const { showBanner, canPrompt, showIosHint, promptInstall, dismiss } = useInstallPrompt()
  const { recentShops, trending } = useHomePageData()

  // Public shops for near me + browse sections
  const { publicShops, fetchPublicShops } = useShopStore()

  // Restore session
  useEffect(() => {
    if (token || user) { setRestoring(false); return }
    const stored = localStorage.getItem('dukkan_token')
    if (!stored) { setRestoring(false); return }
    fetch(apiUrl('/api/auth/me'), { headers: { Authorization: `Bearer ${stored}` } })
      .then(res => {
        if (res.ok) return res.json()
        if (res.status === 401) localStorage.removeItem('dukkan_token')
        return null
      })
      .then(u => { if (u) setAuth(u, stored); setRestoring(false) })
      .catch(() => { setRestoring(false) })
  }, [])

  // Fetch public shops for home sections
  useEffect(() => { fetchPublicShops() }, [])

  // Get user's location for Near You section
  const requestLocation = () => {
    if (!navigator.geolocation) { setLocationDenied(true); return }
    navigator.geolocation.getCurrentPosition(
      pos => setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setLocationDenied(true),
      { enableHighAccuracy: false, timeout: 8000 }
    )
  }
  useEffect(() => { requestLocation() }, [])

  // Poll unread messages
  useEffect(() => {
    if (!user) { setUnreadMsgs(0); return }
    let cancelled = false
    const tick = () => {
      const gid = getGuestId()
      fetch(apiUrl(`/api/messages/unread-count?guestId=${encodeURIComponent(gid)}`), {
        headers: { ...getAuthHeaders() },
      })
        .then(r => r.ok ? r.json() : { unread: 0 })
        .then(d => {
          if (cancelled) return
          const count = Number(d?.unread ?? 0)
          const next = Number.isFinite(count) ? count : 0
          setUnreadMsgs(next)
          try { localStorage.setItem(UNREAD_COUNT_KEY, String(next)) } catch { /* ignore */ }
        })
        .catch(() => {})
    }
    tick()
    const id = window.setInterval(tick, 30000)
    return () => { cancelled = true; window.clearInterval(id) }
  }, [user])

  useEffect(() => {
    const handleUnread = (event: Event) => {
      const count = (event as CustomEvent<number>).detail
      if (typeof count !== 'number' || !Number.isFinite(count)) return
      setUnreadMsgs(count)
    }
    window.addEventListener(UNREAD_EVENT, handleUnread as EventListener)
    return () => window.removeEventListener(UNREAD_EVENT, handleUnread as EventListener)
  }, [])

  // Adapt data for sections
  const nearShops: DkShop[] = useMemo(() => {
    if (!userPos) return []
    return publicShops
      .filter((s): s is typeof s & { location: { lat: number; lng: number } } =>
        s.location !== null && Number.isFinite(s.location?.lat) && Number.isFinite(s.location?.lng)
      )
      .map(s => ({ shop: s, dist: haversineKm(userPos.lat, userPos.lng, s.location.lat, s.location.lng) }))
      .sort((a, b) => a.dist - b.dist)
      .slice(0, 8)
      .map(({ shop }) => ({ id: shop.id, name: shop.name, category: shop.category, address: shop.address }))
  }, [publicShops, userPos])

  const browseShops: DkShop[] = useMemo(
    () => publicShops.slice(0, 6).map(s => ({ id: s.id, name: s.name, category: s.category, address: s.address })),
    [publicShops]
  )
  const trendingDk: DkShop[] = useMemo(() => trending.slice(0, 2).map(fromShopCard), [trending])
  const lastVisitDk: DkShop[] = useMemo(() => recentShops.slice(0, 2).map(fromShopCard), [recentShops])

  const greeting = (() => {
    const h = new Date().getHours()
    if (h < 12) return 'Good morning'
    if (h < 17) return 'Good afternoon'
    if (h < 21) return 'Good evening'
    return 'Good night'
  })()

  return (
    <div className="dk-page">
      {/* Atmospheric backdrop */}
      <div className="dk-atm" aria-hidden />

      {/* ── Header ─────────────────────────────────────────── */}
      <header className="dk-header">
        <div className="dk-greeting">
          {user ? `${greeting}, ${user.name?.split(' ')[0] || 'friend'}` : 'Welcome back'}
        </div>
        <div className="dk-header-main-row">
          <img src="/logo.png" alt="Dukkan" className="dk-logo" />
          <div className="dk-header-actions">
            {/* My Shops button */}
            <button
              className="dk-icon-btn"
              onClick={() => navigate('/shops')}
              title="My Shops"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l1.5-5h15L21 9M4 9v10a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9M3 9h18M9 20v-6h6v6"/>
              </svg>
            </button>

            {/* Messages button */}
            {user && (
              <button
                className="dk-icon-btn"
                onClick={() => navigate('/messages')}
                title="Messages"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                </svg>
                {unreadMsgs > 0 && (
                  <span className="dk-msg-badge">{unreadMsgs > 9 ? '9+' : unreadMsgs}</span>
                )}
              </button>
            )}

            {/* Avatar / Sign in */}
            {user ? (
              <button className="dk-avatar-btn" onClick={() => navigate('/profile')} title="Profile">
                <div className="dk-avatar-inner">
                  {user.picture ? (
                    <img src={user.picture} alt="" referrerPolicy="no-referrer" />
                  ) : (
                    user.name?.charAt(0) || '?'
                  )}
                </div>
              </button>
            ) : (
              <button className="dk-sign-in" onClick={() => navigate('/login')}>
                {t('common:signIn')}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ── PWA Install Banner ─────────────────────────────── */}
      {showBanner && (
        <div className="dk-install-banner">
          <div className="dk-install-body">
            <span className="dk-install-icon">📲</span>
            <div className="dk-install-text">
              <strong>{t('installDukkanty')}</strong>
              {canPrompt && <p>{t('addToHomeScreen')}</p>}
              {showIosHint && (
                <p>
                  {t('iosTapThen')} <span className="dk-ios-share">⬆</span> {t('iosAddToHomeScreen')}
                </p>
              )}
            </div>
          </div>
          <div className="dk-install-actions">
            {canPrompt && (
              <button className="dk-install-btn" onClick={promptInstall}>{t('install')}</button>
            )}
            <button className="dk-install-dismiss" onClick={dismiss}>{t('notNow')}</button>
          </div>
        </div>
      )}

      {/* ── Search bar ────────────────────────────────────── */}
      <div className="dk-search-bar">
        <svg className="dk-search-icon" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="6.5"/><path d="m20 20-3.5-3.5"/>
        </svg>
        <input
          type="text"
          className="dk-search-input"
          placeholder="Search shops, neighbourhoods…"
          value={homeSearch}
          onChange={e => setHomeSearch(e.target.value)}
        />
        {homeSearch && (
          <button className="dk-search-clear" onClick={() => setHomeSearch('')}>×</button>
        )}
      </div>

      <div className="dk-content">
          {/* When searching: hand off to PublicShopBrowser */}
          {homeSearch ? (
            <div className="dk-search-results">
              <PublicShopBrowser embedded initialSearch={homeSearch} />
            </div>
          ) : (
            <>
              {/* ── Hero ──────────────────────────────────── */}
              <div className="dk-hero">
                <div className="dk-hero-halo" aria-hidden />
                <HeroCarousel onScan={() => navigate('/scan')} />
              </div>

              {/* ── Browse All ───────────────────────────── */}
              <section className="dk-section">
                <DkSectionHead
                  title="Browse all"
                  subtitle={publicShops.length > 0 ? `${publicShops.length} shops` : undefined}
                  cta="Show more"
                  onCta={() => navigate('/browse')}
                />

                {browseShops.length > 0 ? (
                  <div className="dk-plaque-grid">
                    {browseShops.map(shop => (
                      <DkPlaqueCard key={shop.id} shop={shop} token={token} onNavigate={navigate} />
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: '40px 16px', textAlign: 'center', color: 'var(--dk-muted)', fontSize: 14 }}>
                    Loading shops…
                  </div>
                )}

                <button className="dk-show-more" onClick={() => navigate('/browse')}>
                  Discover all shops →
                </button>
              </section>

              {/* ── Near You ─────────────────────────────── */}
              <section className="dk-section">
                <DkSectionHead
                  title="Near you"
                  subtitle={nearShops.length > 0 ? `${nearShops.length} shops nearby` : undefined}
                  cta={nearShops.length > 0 ? 'View all' : undefined}
                  onCta={() => navigate('/browse?sort=near')}
                />
                {locationDenied ? (
                  <div className="dk-near-msg">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.45 }}>
                      <path d="M12 2a7 7 0 0 1 7 7c0 5.25-7 13-7 13S5 14.25 5 9a7 7 0 0 1 7-7Z"/><circle cx="12" cy="9" r="2.5"/>
                    </svg>
                    <p>Enable location to discover shops near you</p>
                    <button className="dk-near-enable-btn" onClick={() => { setLocationDenied(false); setUserPos(null); requestLocation() }}>
                      Enable Location
                    </button>
                  </div>
                ) : !userPos ? (
                  <div className="dk-near-msg">
                    <div className="dk-near-spinner" />
                    <p>Detecting your location…</p>
                  </div>
                ) : nearShops.length === 0 ? (
                  <div className="dk-near-msg">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.45 }}>
                      <path d="M12 2a7 7 0 0 1 7 7c0 5.25-7 13-7 13S5 14.25 5 9a7 7 0 0 1 7-7Z"/><circle cx="12" cy="9" r="2.5"/>
                    </svg>
                    <p>No shops found near you yet</p>
                  </div>
                ) : (
                  <div className="dk-slider">
                    {nearShops.map(shop => (
                      <DkPlaqueCard key={shop.id} shop={shop} token={token} onNavigate={navigate} />
                    ))}
                  </div>
                )}
              </section>

              {/* ── Trending Now ─────────────────────────── */}
              {trendingDk.length > 0 && (
                <section className="dk-section">
                  <DkSectionHead
                    title="Trending now"
                    cta="Show more"
                    onCta={() => navigate('/browse?sort=trending')}
                  />
                  <div className="dk-trend-row">
                    {trendingDk.map((shop, i) => (
                      <DkTrendCard key={shop.id} shop={shop} rank={i + 1} token={token} onNavigate={navigate} />
                    ))}
                  </div>
                </section>
              )}

              {/* ── Last Visits ──────────────────────────── */}
              {lastVisitDk.length > 0 && (
                <section className="dk-section">
                  <DkSectionHead
                    title="Your last visits"
                    cta="Show more"
                    onCta={() => navigate('/browse?section=recent')}
                  />
                  <div className="dk-lastvisit-list">
                    {lastVisitDk.map(shop => (
                      <DkLastVisitCard key={shop.id} shop={shop} token={token} onNavigate={navigate} />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
    </div>
  )
}
